// Promotion API Types
export interface Promotion {
  promotion_id: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free_sample' | 'bundle';
  discount_value: string;
  start_date: string;
  end_date: string;
  active: boolean;
}

export interface PromotionsResponse {
  success: boolean;
  data: Promotion[];
  count: number;
  message: string;
}

// Trending Product API Types
export interface TrendingProduct {
  sku: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  pack_size: string;
  price: string;
  trend_score: number;
  youth_trend_score: number;
  brand_name: string;
  brand_tier: 'MAIN' | 'SECONDARY' | 'LIMITED';
}

export interface TrendingProductsResponse {
  success: boolean;
  data: TrendingProduct[];
  count: number;
  limit: number;
  message: string;
}

// Helper functions
export const getDiscountTypeText = (type: string): string => {
  const typeMap: Record<string, string> = {
    'percentage': 'Giảm %',
    'fixed_amount': 'Giảm tiền',
    'free_sample': 'Dùng thử miễn phí',
    'bundle': 'Combo'
  };
  return typeMap[type] || type;
};

export const getDiscountTypeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    'percentage': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900',
    'fixed_amount': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900',
    'free_sample': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900',
    'bundle': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900'
  };
  return colorMap[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
};

export const getBrandTierColor = (tier: string): string => {
  const colorMap: Record<string, string> = {
    'MAIN': 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700',
    'SECONDARY': 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700',
    'LIMITED': 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700'
  };
  return colorMap[tier] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
};

export const getBrandTierText = (tier: string): string => {
  const textMap: Record<string, string> = {
    'MAIN': 'Chính',
    'SECONDARY': 'Phụ',
    'LIMITED': 'Giới hạn'
  };
  return textMap[tier] || tier;
};

export const getTrendScoreColor = (score: number): string => {
  if (score >= 90) return 'text-green-600 dark:text-green-400 hover:text-green-600 dark:hover:text-green-400';
  if (score >= 80) return 'text-blue-600 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-400';
  if (score >= 70) return 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-400';
  return 'text-gray-600 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-400';
};

export const getTrendScoreText = (score: number): string => {
  if (score >= 90) return `Xu hướng cao`;
  if (score >= 80) return `Xu hướng tốt`;
  if (score >= 70) return `Xu hướng khá`;
  return `Xu hướng thấp`;
};

export const formatPromotionDiscount = (type: string, value: string): string => {
  switch (type) {
    case 'percentage':
      return `Giảm ${value}%`;
    case 'fixed_amount':
      return `Giảm ${parseFloat(value).toLocaleString('vi-VN')} VNĐ`;
    case 'free_sample':
      return 'Dùng thử miễn phí';
    case 'bundle':
      return `Giảm ${value}% combo`;
    default:
      return value;
  }
};

export const formatProductPrice = (price: string): string => {
  return `${parseFloat(price).toLocaleString('vi-VN')} VNĐ`;
};

export const isPromotionActive = (startDate: string, endDate: string): boolean => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  return now >= start && now <= end;
};

export const getPromotionStatusColor = (active: boolean, startDate: string, endDate: string): string => {
  if (!active) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
  }
  
  if (isPromotionActive(startDate, endDate)) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900';
  }
  
  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900';
};

export const getPromotionStatusText = (active: boolean, startDate: string, endDate: string): string => {
  if (!active) {
    return 'Không hoạt động';
  }
  
  if (isPromotionActive(startDate, endDate)) {
    return 'Đang diễn ra';
  }
  
  return 'Sắp diễn ra';
};
